import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell, CheckCircle2, Trash2, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, loadAuthToken } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

export const ACTIONS = { call: "Call karna", check_status: "Status check", sms: "SMS/WhatsApp", submit: "Submit karna", work: "Kaam karna", other: "Other" };

export const fuState = (f, now = Date.now()) => {
  const due = new Date(f.due_at).getTime();
  if (f.completed) return "done";
  if (due <= now) return "overdue";
  if (due - (f.alert_before_minutes || 0) * 60000 <= now) return "soon";
  return "upcoming";
};

const isToday = (iso) => new Date(iso).toDateString() === new Date().toDateString();

const NOTIFIED_KEY = "ap_fu_notified";
const notifyDue = (items) => {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  const done = new Set(JSON.parse(localStorage.getItem(NOTIFIED_KEY) || "[]"));
  items.forEach((f) => {
    const st = fuState(f);
    const key = `${f.id}:${st}`;
    if ((st === "soon" || st === "overdue") && !done.has(key)) {
      new Notification(st === "overdue" ? `Overdue: ${f.request_id}` : `Jaldi due: ${f.request_id}`, {
        body: `${f.customer_name} · ${ACTIONS[f.action] || f.kind} · ${f.instructions}`,
        tag: key,
      });
      done.add(key);
    }
  });
  localStorage.setItem(NOTIFIED_KEY, JSON.stringify([...done].slice(-300)));
};

export const usePendingFollowups = (enabled) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!enabled) { setItems([]); return; }
    const load = async () => {
      try { const r = await api.get("/followups", { params: { pending: true } }); setItems(r.data || []); notifyDue(r.data || []); }
      catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [enabled]);
  return items;
};

export const STAFF_PATHS = ["/admin", "/central", "/branch", "/workspace", "/followups"];

export const FollowupBell = () => {
  const { pathname } = useLocation();
  const [authed, setAuthed] = useState(false);
  const staffPage = STAFF_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/")) && pathname !== "/admin/login";
  useEffect(() => { setAuthed(staffPage && !!loadAuthToken() && !!localStorage.getItem("ap_admin_user")); }, [pathname, staffPage]);
  useEffect(() => {
    if (authed && typeof Notification !== "undefined" && Notification.permission === "default") Notification.requestPermission();
  }, [authed]);
  const items = usePendingFollowups(authed);
  if (!authed) return null;
  const overdue = items.filter((f) => fuState(f) === "overdue").length;
  const alertCount = items.filter((f) => ["overdue", "soon"].includes(fuState(f))).length;
  return (
    <Link to="/followups" data-testid="followup-bell"
      className={`relative p-2 rounded-md border transition-colors ${overdue ? "border-red-300 bg-red-50 text-red-600 animate-pulse" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}
      aria-label="Follow-ups">
      <Bell className="w-4 h-4" />
      {alertCount > 0 && (
        <span data-testid="followup-bell-count" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">{alertCount}</span>
      )}
    </Link>
  );
};

export const FollowupCard = ({ f, onChange, showLead = true }) => {
  const st = fuState(f);
  const complete = async () => {
    try { await api.post(`/followups/${f.id}/complete`); toast.success("Done mark ho gaya"); onChange(); } catch { toast.error("Failed"); }
  };
  const remove = async () => {
    if (!window.confirm("Ye follow-up delete karein?")) return;
    try { await api.delete(`/followups/${f.id}`); toast.success("Deleted"); onChange(); } catch { toast.error("Failed"); }
  };
  const tone = { overdue: "border-red-400 bg-red-50 animate-pulse", soon: "border-amber-300 bg-amber-50", upcoming: "border-slate-200 bg-white", done: "border-green-200 bg-green-50/40 opacity-75" }[st];
  return (
    <div data-testid={`fu-card-${f.id}`} className={`rounded-lg border p-4 ${tone}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge className={f.kind === "delay" ? "bg-orange-100 text-orange-800" : "bg-teal-100 text-teal-800"}>{f.kind === "delay" ? "Delay" : "Follow-up"}</Badge>
            {f.action && <Badge className="bg-slate-100 text-slate-700">{ACTIONS[f.action] || f.action}</Badge>}
            {st === "overdue" && <Badge className="bg-red-600 text-white"><AlertTriangle className="w-3 h-3 mr-1" />Overdue</Badge>}
            {st === "soon" && <Badge className="bg-amber-500 text-white">Jaldi due</Badge>}
            {st === "done" && <Badge className="bg-green-600 text-white">Done</Badge>}
          </div>
          {showLead && (
            <Link to={f.lead_id ? `/workspace/${f.lead_id}` : f.customer_id ? `/admin/customers/${f.customer_id}` : "/admin/customers"} className="block mt-2 text-sm font-semibold text-slate-900 hover:text-teal-700" data-testid={`fu-lead-link-${f.id}`}>
              {f.request_id} · {f.customer_name} <span className="font-normal text-slate-500">· {f.service_name}</span>
            </Link>
          )}
          <div className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">{f.instructions}</div>
          {f.reason && <div className="mt-1 text-xs text-slate-600">Reason: {f.reason}</div>}
          <div className="mt-2 text-xs text-slate-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> {new Date(f.due_at).toLocaleString()} · by {f.created_by}{f.assigned_to ? ` · for ${f.assigned_to}` : ""}
          </div>
        </div>
        {!f.completed && (
          <div className="flex gap-2">
            <Button size="sm" onClick={complete} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`fu-done-${f.id}`}><CheckCircle2 className="w-4 h-4 mr-1" />Done</Button>
            <Button size="sm" variant="outline" onClick={remove} className="text-red-600" data-testid={`fu-delete-${f.id}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const groupFollowups = (items) => {
  const pending = items.filter((f) => !f.completed);
  return {
    overdue: pending.filter((f) => fuState(f) === "overdue"),
    today: pending.filter((f) => fuState(f) !== "overdue" && isToday(f.due_at)),
    upcoming: pending.filter((f) => fuState(f) !== "overdue" && !isToday(f.due_at)),
  };
};
